import React, { useEffect, useImperativeHandle, useState, useRef, useMemo } from "react";
import { DialogContent, Modal } from "@fluentui/react";
import { getBotProtectionVerificationStyles } from "./styles";
import { RingProvider } from "@src/app/classes/RingProvider";

export interface BotProtectionVerificationProps {
    tokenServiceApi: string;
}

const CLOCK_SKEW = 2 * 60 * 1000;

interface IBotVerificationToken {
    access_token: string;
    expires_in: number;
    expires_on: Date;
}

export interface BotProtectionVerificationRef {
    getVerificationToken: () => Promise<string>;
}

const BotProtectionVerification = React.forwardRef<BotProtectionVerificationRef, BotProtectionVerificationProps>((props, ref) => {
    const [showChallenge, setShowChallenge] = useState<boolean>(false);
    const [iframeVisible, setIframeVisible] = useState<boolean>(false);

    const explicitVerificationRequestedRef = useRef<boolean>(false);
    const interactionRequiredRef = useRef<boolean>(false);

    const botVerificationTokenRef = useRef<IBotVerificationToken>(null);
    const verificationInProgressPromiseRef = useRef<Promise<void>>(null);
    const verificationInProgressRef = useRef<boolean>(false);

    const intervalIdRef = useRef<number>(null);
    const verificationErrorRef = useRef<boolean>(false);
    const loadingTimeoutIdRef = useRef<number>(null);

    const styles = getBotProtectionVerificationStyles(iframeVisible);

    const iframe = useMemo(() => {
        const iframe = document.createElement("iframe");
        iframe.setAttribute('id', '__botProtectionIframe');
        return iframe;
    }, []);

    const iframeContainer = useMemo(() => {
        const container = document.createElement("div");
        container.setAttribute('id', '__botProtectionIframeContainer');
        container.setAttribute('class', styles.iframeContainer);
        container.appendChild(iframe);
        document.body.appendChild(container);
        return container;
    }, []);

    iframeContainer.setAttribute('class', styles.iframeContainer);
    iframe.setAttribute('class', styles.iframe);
    if (RingProvider.get() == "local" || window.location.hostname === "localhost") {
        iframe.setAttribute('allow', 'local-network-access *');
    }

    useImperativeHandle(ref, () => ({
        getVerificationToken
    }), []);

    const initializeVerification = (): Promise<void> => {
        if (verificationErrorRef.current) {
            //if error, return promise that never resolves
            //we are at the point where we wait for the user to reload the page
            return new Promise(() => {

            });
        }
        let resolvePromise: (value: void | PromiseLike<void>) => void;
        let rejectPromise: (reason: any) => void;

        loadingTimeoutIdRef.current = window.setTimeout(async () => {
            verificationErrorRef.current = true;
            setShowChallenge(false);
            await Xrm.Navigation.openErrorDialog({
                message: window.TALXIS.Portal.Translations.getLocalizedString('components/utilities/BotProtectionVerification/LoadError'),
                details: "timeout"
            });

            window.location.reload();
        }, 5 * 1000);

        interactionRequiredRef.current = false;
        verificationInProgressRef.current = true;

        verificationInProgressPromiseRef.current = new Promise<void>((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });
        iframe.src = getRedirectUrl();

        monitorIframeForHash(iframe).then(async (hash) => {
            const params = new URLSearchParams(hash.substring(1));

            if (params.get("error")) {
                verificationErrorRef.current = true;
                const error = params.get("error");
                const errorDescription = params.get("error_description");
                setShowChallenge(false);
                // TODO: We should enable retry in a smart way to either reject existing pending promises or let them resolve with the new token, but refresh is the safest option right now
                await Xrm.Navigation.openErrorDialog({
                    message: window.TALXIS.Portal.Translations.getLocalizedString('components/utilities/BotProtectionVerification/Error'),
                    details: "Bot protection error: " + error + " - " + errorDescription
                });
                window.location.reload();
            }
            else {
                const expiresOn = new Date();
                expiresOn.setSeconds(expiresOn.getSeconds() + parseInt(params.get("expires_in")));
                botVerificationTokenRef.current = {
                    access_token: params.get("access_token"),
                    expires_in: parseInt(params.get("expires_in")),
                    expires_on: expiresOn
                };
                setShowChallenge(false);
                verificationInProgressRef.current = false;
                resolvePromise();
            }
        });

        return verificationInProgressPromiseRef.current;
    };

    const monitorIframeForHash = (iframe: HTMLIFrameElement): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            if (intervalIdRef.current) {
                // TODO: Reject the previous promise here
                window.clearInterval(intervalIdRef.current);
            }
            intervalIdRef.current = window.setInterval(() => {
                let href: string = "";
                const contentWindow = iframe.contentWindow;
                try {
                    href = contentWindow.location.href;
                } catch (e) { }

                if (!href || href === "about:blank") {
                    return;
                }

                window.clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
                resolve(iframe.contentWindow.location.hash);
            }, 100);
        });
    };

    const getVerificationToken = async (force: boolean = false): Promise<string> => {
        explicitVerificationRequestedRef.current = true;

        const isTokenValid = botVerificationTokenRef.current && botVerificationTokenRef.current.expires_on.getTime() - CLOCK_SKEW > new Date().getTime();

        if (!verificationInProgressPromiseRef.current || force || (!isTokenValid && !verificationInProgressRef.current)) {
            verificationInProgressPromiseRef.current = initializeVerification();
        }

        await verificationInProgressPromiseRef.current;
        setShowChallenge(false);
        return botVerificationTokenRef.current.access_token;
    };

    const getRedirectUrl = () => {
        return props.tokenServiceApi + "/turnstile/v1.0/challenge?redirect_uri=" + window.location.origin + "/shared/blank.html"; //&debug=interact";
    };

    const receiveMessage = async (event: MessageEvent) => {
        if (event.origin !== props.tokenServiceApi) {
            return;
        }

        switch (event.data.type) {
            case "bot_protection_error":
                verificationInProgressRef.current = false;
                verificationErrorRef.current = true;
                setShowChallenge(false);
                // TODO: We should enable retry in a smart way to either reject existing pending promises or let them resolve with the new token, but refresh is the safest option right now
                await Xrm.Navigation.openErrorDialog({
                    message: window.TALXIS.Portal.Translations.getLocalizedString('components/utilities/BotProtectionVerification/Error'),
                    details: "Bot protection error: " + event.data.error
                });
                window.location.reload();
                break;
            case "bot_protection_interaction_start":
                interactionRequiredRef.current = true;
                if (explicitVerificationRequestedRef.current) {
                    setShowChallenge(true);
                }
                break;
            case "bot_protection_interaction_end":
                verificationInProgressRef.current = false;
                break;
            case "bot_protection_loaded":
                if (loadingTimeoutIdRef.current) {
                    window.clearTimeout(loadingTimeoutIdRef.current);
                }
                break;
        }
    };
    useEffect(() => {
        window.addEventListener("message", receiveMessage);
        return () => {
            window.removeEventListener("message", receiveMessage);
            if (intervalIdRef.current) {
                window.clearInterval(intervalIdRef.current);
            }
        };
    }, []);

    useEffect(() => {
        queueMicrotask(() => {
            setIframeVisible(showChallenge);
        });
    }, [showChallenge]);

    return (
        <>
            {showChallenge &&
                <Modal
                    isOpen={true}
                    isBlocking
                    styles={{
                        main: styles.dialogMain
                    }}
                >
                    <DialogContent
                        title={window.TALXIS.Portal.Translations.getLocalizedString("components/utilities/BotProtectionVerification/Title")}
                        subText={window.TALXIS.Portal.Translations.getLocalizedString("components/utilities/BotProtectionVerification/Desription")}
                    >
                    </DialogContent>
                </Modal>
            }
            {/*
                // Dialog use is blocked by https://github.com/microsoft/fluentui/issues/30701
                <Dialog
                    title={window.TALXIS.Portal.Translations.getLocalizedString("components/utilities/BotProtectionVerification/Title")}
                    subText={window.TALXIS.Portal.Translations.getLocalizedString("components/utilities/BotProtectionVerification/Desription")}
                    className={styles.root} minWidth={365} styles={{ root: { display: showChallenge ? null : "none" } }} hidden={false}
                >
                    <div ref={containerRef} id="botProtectionIframeContainer"></div>
                </Dialog>
            */}
        </ >
    );
});

export default BotProtectionVerification;